import { useContext, useRef, useState } from "react";

import { PlusIcon as PlusIconSolid } from "@heroicons/react/16/solid";
import { PlusIcon } from "@heroicons/react/24/outline";
import { Button } from "@upstageai/ui";
import { useTranslation } from "react-i18next";

import FileInput from "./FileInput";
import FileSecurityWarning from "./FileSecurityWarning";
import DemoSelectDialog from "../../components/Dialog/DemoSelectDialog";
import ErrorDialog from "../../components/Dialog/ErrorDialog";
import { BaseTooltip } from "../../components/Tooltip/BaseTooltip";
import { getShowConfig } from "../../constants/Endpoints";
import { FILE_UPLOAD_ON } from "../../constants/Features";
import { ValidatorFileContext } from "../../hooks/useFile";
import { ValidatorModelsContext } from "../../hooks/useFile";
import { fileUpload } from "../../hooks/useImageUploader";
import { FILE_PREVIEW_STYLES } from "../../styles/filePreview";
import { FileUploadConfig } from "../../types/upload";
import { FileUploadCallbacks } from "../../types/upload";
import { configStore } from "../../utils/mobx/ConfigStore";
import { endpointStore } from "../../utils/mobx/EndpointStore";
import { envVarStore } from "../../utils/mobx/EnvVarStore";
import { cn } from "../../utils/style";

interface FileUploadButtonProps {
  isExpanded: boolean;
  onFileUpload?: () => void;
}

export default function FileUploadButton({
  isExpanded,
  onFileUpload = () => {},
}: FileUploadButtonProps) {
  const { t } = useTranslation();
  const { multifileUpload } = envVarStore;
  const { endpointType } = endpointStore;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [demoSelectDialogOpen, setDemoSelectDialogOpen] =
    useState<boolean>(false);

  const [errorOn, setErrorOn] = useState<boolean>(false);
  const [errorType, setErrorType] = useState<number>(1);
  const [errorCloseFunction, setErrorCloseFunction] = useState<() => void>(
    () => {},
  );
  const [errorReloadFunction, setErrorReloadFunction] = useState<() => void>(
    () => {},
  );

  const { models, selectedModelIndex } = useContext(ValidatorModelsContext);
  const {
    files,
    setSelectedFileIndex,
    setFiles,
    setInferenceState,
    inferenceState,
  } = useContext(ValidatorFileContext);

  const fileUploadConfig: FileUploadConfig = {
    inputFiles: [] as File[],
    models: models,
    selectedModelIndex: selectedModelIndex,
    files: files,
    errorType: errorType,
    skipValidation: false,
  };

  const fileUploadCallback: FileUploadCallbacks = {
    setErrorType: setErrorType,
    setWorkPercentageVisible: () => {},
    setIsOpen: () => {},
    setErrorOn: setErrorOn,
    setInferenceState: setInferenceState,
    setSelectedFileIndex: setSelectedFileIndex,
    setFiles: setFiles,
    setErrorCloseFunction: setErrorCloseFunction,
    setErrorReloadFunction: setErrorReloadFunction,
  };

  const handleFileUpload = async (_files: FileList) => {
    if (_files.length === 0) return;

    fileUploadConfig.inputFiles = Array.from(_files);
    if (getShowConfig(endpointType)) {
      // 마지막으로 적용된 config 사용
      if (endpointType === "ocr") {
        fileUploadConfig.inferenceConfig = configStore.currentOcrConfig;
      } else if (endpointType === "layout-analysis") {
        fileUploadConfig.inferenceConfig = configStore.currentDPConfig;
      }
    }

    await fileUpload(fileUploadConfig, fileUploadCallback);
    onFileUpload();
  };

  const handleFileUploadButtonClick = () => {
    if (!FILE_UPLOAD_ON) return;

    if (endpointType.toLowerCase() === "extractor") {
      setDemoSelectDialogOpen(prev => !prev);
      onFileUpload();
      return;
    }

    fileInputRef.current?.click();
  };

  const getUploadButtonIcon = () =>
    isExpanded ? (
      <PlusIcon className="size-6 text-muted-darker" />
    ) : (
      <PlusIconSolid className="size-4 text-muted-darker" />
    );

  const UploadButton = () => {
    const buttonProps = {
      variant: "ghost" as const,
      className: cn(
        "flex items-center justify-center",
        isExpanded
          ? cn(
              FILE_PREVIEW_STYLES.base,
              FILE_PREVIEW_STYLES.upload,
              FILE_PREVIEW_STYLES.sizes.expanded,
            )
          : "w-8 h-8 rounded-md border border-muted p-0",
        "bg-white border border-border-medium border-dashed",
        inferenceState.isInfering && "opacity-50 cursor-not-allowed",
      ),
      disabled: inferenceState.isInfering,
      onClick: handleFileUploadButtonClick,
    };

    return (
      <BaseTooltip
        tooltipContent={
          <div className="ml-4 max-w-[280px]">
            {t("actions.add_custom_file")}
            <FileSecurityWarning />
          </div>
        }
        side="bottom"
      >
        <Button {...buttonProps}>{getUploadButtonIcon()}</Button>
      </BaseTooltip>
    );
  };

  const hasCustomFile = files.some(file => !file.documentInfo);

  return (
    <>
      <div
        className={cn(
          "flex h-fit items-center self-end bg-muted p-1.5",
          hasCustomFile ? "rounded-s-lg" : "rounded-lg",
          isExpanded ? "mb-2.5" : "mb-1",
          !isExpanded && "justify-center",
        )}
      >
        <UploadButton />
      </div>

      <FileInput
        ref={fileInputRef}
        onFileSelect={handleFileUpload}
        multiple={multifileUpload === "true"}
      />
      <ErrorDialog
        open={errorOn}
        setOpen={setErrorOn}
        errorType={errorType}
        errorCloseFunction={errorCloseFunction}
        errorReloadFunction={errorReloadFunction}
      />

      <DemoSelectDialog
        isOpen={demoSelectDialogOpen}
        setIsOpen={setDemoSelectDialogOpen}
      />
    </>
  );
}
